import { MutationCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import axios from 'axios'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { AuthProvider } from './auth/AuthProvider'
import { UserContextProvider } from './providers/UserContextProvider'
import { TerminologyProvider } from './providers/TerminologyProvider'
import { ThemeProvider } from './providers/ThemeProvider'
import { ToastProvider } from './components/ui/Toast'
import { ErrorBoundary } from './components/ErrorBoundary'
import { getApiErrorMessage, notifyMutationError } from './lib/api-errors'
import './i18n'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    // Fallback for mutations without their own onError: a failed write must
    // never be silent, and the server's message beats a generic one (F-39).
    // 403s are handled by the forbidden notifier in api/client.ts.
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError) return
      if (axios.isAxiosError(error) && error.response?.status === 403) return
      notifyMutationError(getApiErrorMessage(error))
    },
  }),
})

export function mountApp() {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <UserContextProvider>
            <TerminologyProvider>
            <ThemeProvider>
              <ToastProvider>
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </ToastProvider>
            </ThemeProvider>
            </TerminologyProvider>
            </UserContextProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}
